import React from 'react';
import { Field, useField, useFormState } from 'react-final-form';
import { FormControl, Checkbox, InputGroup } from 'react-bootstrap';

const InputField = ({ input, ...rest }) => <FormControl {...input} {...rest} />;

const MoneyField = ({ input, ...rest }) => (
  <InputGroup>
    <InputGroup.Addon>$</InputGroup.Addon>
    <FormControl bsSize="small" type="text" {...input} {...rest} />
  </InputGroup>
);

const CheckboxField = ({ input, ...rest }) => (
  <Checkbox {...input} {...rest}>
    {rest.title}
  </Checkbox>
);

function ScheduleDCreditorFields({ creditor, index, onRemove, onMoveDown, onMoveUp, onAdd }) {
  // Subscribe to changes to creditor value
  // This is used to show/hide extra fields based on answers to some yes/no questions
  const {
    input: { value: creditorValue },
  } = useField(creditor);

  return (
    <table>
      <thead>
        <tr>
          <td colSpan="2">
            2.{index + 1}.{' '}
            {onAdd && (
              <div className="btn btn-success btn-sm" onClick={onAdd} title="Insert new creditor">
                +
              </div>
            )}
            {onMoveUp && (
              <div className="btn btn-primary btn-sm" onClick={onMoveUp} title={`Swap this creditor with 2.${index}`}>
                ▲
              </div>
            )}
            {onMoveDown && (
              <div
                className="btn btn-primary btn-sm"
                onClick={onMoveDown}
                title={`Swap this creditor with 2.${index + 2}`}
              >
                ▼
              </div>
            )}
            <div className="btn btn-danger btn-sm" onClick={onRemove} title="Remove creditor">
              X
            </div>
          </td>
          <td className="claim-amount">
            <label htmlFor="">
              <span className="label">Amount of claim</span>
              <span className="schedule-tips">Do not deduct the value of collateral.</span>
            </label>
          </td>
          <td className="collateral-value">
            <label htmlFor="">
              <span className="label">Value of collateral that supports this claim</span>
            </label>
          </td>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>
            <label>
              <span className="label">Creditor's name</span>
            </label>
            <Field name={`${creditor}.creditor_name`} component={InputField} type="text" size="lg" placeholder="" />
          </td>
          <td>
            <label>
              <span className="label">Describe debtor’s property that is subject to a lien</span>
            </label>
            <Field
              name={`${creditor}.property_description`}
              component={InputField}
              componentClass="textarea"
              rows="3"
              cols="30"
              id={`property_description-${index}`}
            />
          </td>
          <td className="money">
            <Field
              name={`${creditor}.claim_amount_cents`}
              component={MoneyField}
              id={`claim-amount-${index}`}
              size={10}
            />
          </td>
          <td className="money">
            <Field
              name={`${creditor}.collateral_amount_cents`}
              component={MoneyField}
              id={`collateral_amount_cents-${index}`}
              size={10}
            />
          </td>
        </tr>
        <tr>
          <td>
            <label>
              <span className="label">Creditor's mailing address</span>
            </label>
            <br />

            <label>
              <span className="label">Street</span>
            </label>
            <Field name={`${creditor}.creditor_address_street`} component={InputField} placeholder="Street" />

            <label>
              <span className="label">City</span>
            </label>
            <Field name={`${creditor}.creditor_address_city`} component={InputField} placeholder="City" />

            <label>
              <span className="label">State</span>
            </label>
            <Field name={`${creditor}.creditor_address_state`} component={InputField} placeholder="State" />

            <label>
              <span className="label">Zip Code</span>
            </label>
            <Field name={`${creditor}.creditor_address_zip_code`} component={InputField} placeholder="Zip Code" />

            <label>
              <span className="label">Country</span>
            </label>
            <Field name={`${creditor}.creditor_address_country`} component={InputField} placeholder="Country" />
          </td>
          <td>
            <label>
              <span className="label">Describe the lien</span>
            </label>
            <Field
              name={`${creditor}.lien_description`}
              component={InputField}
              componentClass="textarea"
              rows="3"
              cols="30"
              id={`lien_description-${index}`}
            />
          </td>
        </tr>
        <tr>
          <td>
            <label>
              <span className="label">Creditor's email address, if known</span>
            </label>
            <Field name={`${creditor}.creditor_email`} component={InputField} type="text" placeholder="" />
          </td>
          <td>
            <label htmlFor="">
              <span className="label">Is creditor an insider or related party?</span>
            </label>
            <br />
            <Field name={`${creditor}.is_creditor_an_insider`} component={InputField} componentClass="select">
              <option value=""></option>
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </Field>
          </td>
        </tr>
        <tr>
          <td>
            <label htmlFor="">
              <span className="label">Date debt was incurred</span>
            </label>
            <Field name={`${creditor}.debt_date`} component={InputField} type="text" placeholder="" />
          </td>
          <td>
            <label htmlFor="">
              <span className="label">Is anyone else liable on this claim?</span>
            </label>
            <br />
            <Field name={`${creditor}.is_anyone_else_liable`} component={InputField} componentClass="select">
              <option value=""></option>
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </Field>
          </td>
        </tr>
        <tr>
          <td>
            <label>
              <span className="label">Last 4 digits of account number</span>
            </label>
            <Field
              name={`${creditor}.account_number_last_4_digits`}
              component={InputField}
              type="text"
              placeholder=""
            />
          </td>
          <td rowSpan="2">
            <label>
              <span className="label">As of the petition filing date, the claim is:</span>
              <span className="schedule-tips">Check all that apply.</span>
            </label>
            <br />
            <Field
              name={`${creditor}.is_contingent`}
              component={CheckboxField}
              type="checkbox"
              title="Contingent"
            />{' '}
            <Field
              name={`${creditor}.is_unliquidated`}
              component={CheckboxField}
              type="checkbox"
              title="Unliquidated"
            />{' '}
            <Field name={`${creditor}.is_disputed`} component={CheckboxField} type="checkbox" title="Disputed" />{' '}
          </td>
        </tr>
        <tr>
          <td>
            <label htmlFor="">
              <span className="label multiple-interest">
                Do multiple creditors have an interest in the same property?
              </span>
            </label>
            <Field name={`${creditor}.has_multiple_creditor_interest`} component={InputField} componentClass="select">
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </Field>
            <div
              className="is-multiple-creditors-interested-yes"
              style={{
                display: creditorValue.has_multiple_creditor_interest === 'yes' ? 'inherit' : 'none',
              }}
            >
              {index === 0 ? (
                <React.Fragment>
                  <span className="creditor-priority">
                    Specify each creditor, including this creditor, and its relative priority
                  </span>
                  <Field
                    name={`${creditor}.creditor_priority`}
                    component={InputField}
                    componentClass="textarea"
                    rows="3"
                    cols="30"
                    id={`creditor-priority-${index}`}
                  />
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <span className="has-specified-creditor-priority">
                    Have you already specified the relative priority?
                  </span>
                  <Field
                    name={`${creditor}.has_already_specified_creditor_priority`}
                    component={InputField}
                    componentClass="select"
                  >
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </Field>
                  <div
                    className="has-specified-creditor-priority-no"
                    style={{
                      display: creditorValue.has_already_specified_creditor_priority === 'no' ? 'inherit' : 'none',
                    }}
                  >
                    <span className="creditor-priority">
                      Specify each creditor, including this creditor, and its relative priority
                    </span>
                    <Field
                      name={`${creditor}.creditor_priority`}
                      component={InputField}
                      componentClass="textarea"
                      rows="3"
                      cols="30"
                      id={`creditor-priority-${index}`}
                    />
                  </div>
                  <div
                    className="has-specified-creditor-priority-yes"
                    style={{
                      display: creditorValue.has_already_specified_creditor_priority === 'yes' ? 'inherit' : 'none',
                    }}
                  >
                    <span className="creditor-priority">The relative priority of creditors is specified on lines</span>{' '}
                    <Field
                      name={`${creditor}.creditor_priority_specified_on_line`}
                      component={InputField}
                      id={`creditor_priority_specified_on_line-${index}`}
                      type="text"
                      defaultValue={creditor.creditor_priority_specified_on_line}
                    />
                  </div>
                </React.Fragment>
              )}
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  );
}

export default ScheduleDCreditorFields;



// WEBPACK FOOTER //
// ./src/scheduleform/schedule_d/ScheduleDCreditorFields.jsx